import {
  AgencySettings,
  IntegrationEnum,
  IntegrationStatus,
} from 'models/InternalIntegrationSetting';
import useFetchIntegrationAgencySettings from 'hooks/useFetchIntegrationAgencySettings';
import { IntegrationAccess } from 'utils/integrations/integrationAccessUrl';

const extractAccessUrl = (agencySettings: AgencySettings | undefined) => {
  const customSettings = agencySettings?.customIntegrationSettings;

  return customSettings && 'accessUrl' in customSettings
    ? customSettings.accessUrl
    : undefined;
};

/**
 * Access URL for the products that carry it in their agency settings. One
 * implementation of `FetchIntegrationAccess`: products served by a dedicated
 * endpoint need their own hook.
 */
const useAgencySettingsAccessUrl = ({
  enumId,
  enabled = true,
}: {
  enumId: IntegrationEnum;
  enabled?: boolean;
}) => {
  const {
    data: integrationAgencySettings,
    isInitialLoading: isLoading,
    isFetching,
    refetch,
  } = useFetchIntegrationAgencySettings({ enumId, enabled });

  const fetchAccessUrl = async (): Promise<IntegrationAccess> => {
    // A failed refetch resolves rather than rejects, and keeps the previous
    // payload in `data` — returning it would hand back a stale, expired link.
    const { data, isError } = await refetch();

    if (isError) {
      return { status: 'unavailable' };
    }

    const freshSettings = data?.agencySettings;
    const accessUrl = extractAccessUrl(freshSettings);

    if (accessUrl) {
      return { status: 'available', accessUrl };
    }

    return freshSettings?.status === IntegrationStatus.PENDING
      ? { status: 'accountPending' }
      : { status: 'unavailable' };
  };

  return {
    integration: integrationAgencySettings?.integration,
    agencySettings: integrationAgencySettings?.agencySettings,
    isLoading,
    isFetching,
    fetchAccessUrl,
  };
};

export default useAgencySettingsAccessUrl;
